// Строка списка раздела. Одна грамматика на все представления всех разделов (правило 2):
// заголовок, подпись под ним и метка справа. Смена смысла строки — это другое
// представление, а не другой компонент, поэтому строка в системе ровно одна.
//
// Слева необязательный слот выбора: он нужен там, где над множеством строк есть действие
// (правило 3). Выбор рисуется ВНЕ ссылки — иначе нажатие на галочку уводило бы со страницы
// вместо того, чтобы отметить строку.

import type { ReactNode } from 'react';

import { Link } from 'react-router';

import { ПРИЗНАК_СТРОКИ } from '@/layout/клавиатура-списка';
import { cn } from '@/utils/cn';

export interface ListRowProps {
  /** Куда ведёт строка: адрес с уже проставленным состоянием экрана. */
  to: string;
  selected: boolean;
  title: string;
  /** Что за строка: заказчик, город, пояс — одной строкой, через разделитель. */
  subtitle: ReactNode;
  /** Метка справа: «закрыт», разряд поста, число объектов. */
  badge?: ReactNode;
  /**
   * Величина справа НА ЛИНИИ ЗАГОЛОВКА: сумма выдачи, а не метка.
   *
   * Метка стоит на второй линии, у подписи, и это верно для слов: она не должна отнимать место
   * у заголовка. Деньги — другое дело: по столбцу сумм строки сравнивают глазом, и сумма на
   * второй линии, вслед за длинной подписью, этого столбца не образует. Ровно поэтому пульт
   * подотчёта когда-то завёл свою строку (`PersonRow`); этот параметр даёт то же самое общей
   * строке. Цифры моноширинные, знак рубля не ставится: единицу называет заголовок блока.
   * Не задана — строка рисуется ровно как раньше.
   */
  value?: ReactNode;
  /**
   * Поле 20 точек вместо 16: список лежит внутри `Block`, а не в колонке.
   *
   * Колонка списка держит поле 16 (ПРАВИЛА, «Свой отступ»), блок — 20. Строка, положенная в блок
   * с полем колонки, стоит на четыре точки левее заголовка этого же блока: значок и слова
   * шапки на одной вертикали, а текст строк под ними на другой.
   */
  blockInset?: boolean;
  /** Слот выбора строки слева: галочка для действий над множеством. */
  select?: ReactNode;
  /**
   * Переносить длинный заголовок на следующую строку, а не резать многоточием.
   *
   * По умолчанию заголовок в одну строку: фамилия короткая, а лишняя высота строки — это
   * человек, не поместившийся на экран. В реестре ведомостей заголовок — название объекта
   * вместе с заказчиком, адресом и городом, и многоточие съедает ровно то, чем объекты
   * различаются: «"Компания Шлюмберже" Радиус-Сервис фе…» стояло дважды подряд, у двух разных
   * объектов. 11.10.2026 заказчик попросил длинные названия переносить.
   */
  wrapTitle?: boolean;
  /**
   * Переносить подпись, а не резать её многоточием.
   *
   * По умолчанию подпись в одну строку, по той же причине, что и заголовок. Но бывает подпись,
   * в которой хвост и есть ответ: в панели человека строка соседней ведомости месяца перечисляет,
   * что в ней удержано, — «Черновик · аванс 20 000 · официальная часть 51 869 · форма 1 000», —
   * и на ширине панели (384 точки) многоточие съедало ровно «форму», ради которой строку читают.
   */
  wrapSubtitle?: boolean;
  /**
   * Держать галочку видимой постоянно.
   *
   * По умолчанию она появляется под курсором и при переходе на неё с клавиатуры. Причина
   * замерена по кадру: в реестре людей 286 пустых квадратов стояли вдоль всего списка
   * ПОСТОЯННО, а действие над множеством нужно раз в неделю. Шум всегда, польза изредка.
   *
   * Постоянной она становится, как только выбрана хоть одна строка: человек уже в режиме
   * выбора, и прятать от него остальные галочки — значит заставлять водить мышью по списку,
   * чтобы найти, куда нажимать.
   */
  selectVisible?: boolean;
}

export function ListRow({
  to,
  selected,
  title,
  subtitle,
  badge,
  value,
  blockInset = false,
  select,
  selectVisible = false,
  wrapTitle = false,
  wrapSubtitle = false,
}: ListRowProps) {
  const заголовок = cn(
    wrapTitle ? 'min-w-0 break-words' : 'truncate',
    'text-label-sm text-text-strong-950',
  );
  const body = (
    <Link
      to={to}
      aria-current={selected ? 'true' : undefined}
      // Признак строки для хода по клавиатуре. Ссылки в колонке есть и кроме строк —
      // «Новый заказчик», подписи отборов, — и стрелка обязана идти по строкам, а не по
      // всему, что нажимается.
      {...{ [ПРИЗНАК_СТРОКИ]: '' }}
      className={cn(
        // Отступ 4 точки, а не 10.
        //
        // Замер по кадру 04.09.2026: строка выходила 67 точек при принятой норме 44-48
        // (`ПРАВИЛА.md`, плотность). На экран помещалось двенадцать человек из 286 — на треть
        // меньше, чем должно, и это самый длинный список продукта.
        'flex min-w-0 flex-1 items-center gap-2 py-1 transition duration-200 ease-out',
        // Кольцо фокуса — внутрь, а не наружу: наружное кольцо у строки во всю ширину
        // колонки срезается её краем и видно с трёх сторон из четырёх. Без кольца человек,
        // идущий стрелками, просто не знает, где он.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-base',
        select === undefined && (blockInset ? 'px-5' : 'px-4'),
        select === undefined && (selected ? 'bg-bg-weak-50' : 'hover:bg-bg-weak-50'),
      )}
    >
      {/* Заголовок занимает ВСЮ ширину строки, метки уходят на вторую линию к подписи.
          Пока метки стояли справа от заголовка, обрезался именно он: метки не сжимаются, а
          «Межвахтовый отдых» рядом с «долг» съедает половину колонки. На живом экране это
          выглядело как «Багровский С…» с двумя метками — то есть строка приносила в жертву
          ровно то, ради чего человек в неё смотрит. */}
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        {value === undefined ? (
          <span className={заголовок}>{title}</span>
        ) : (
          <span className='flex min-w-0 items-baseline justify-between gap-3'>
            <span className={заголовок}>{title}</span>
            <span className='shrink-0 text-label-sm tabular-nums text-text-strong-950'>
              {value}
            </span>
          </span>
        )}
        <span className='flex min-w-0 items-center gap-2'>
          <span
            className={cn(
              'min-w-0 flex-1 text-paragraph-xs text-text-sub-600',
              wrapSubtitle ? 'break-words' : 'truncate',
            )}
          >
            {subtitle}
          </span>
          {/* `flex`, а не строчный span: у строчного своя высота строки, и обёртка вокруг
              метки в 20 точек занимала 24. Четыре точки на каждой из 286 строк — это ещё
              один человек, не поместившийся на экран. */}
          {badge !== undefined && <span className='flex shrink-0'>{badge}</span>}
        </span>
      </span>
    </Link>
  );

  if (select === undefined) {
    return <div className='flex border-b border-stroke-soft-200'>{body}</div>;
  }

  return (
    <div
      className={cn(
        'group/row flex items-center gap-3 border-b border-stroke-soft-200 transition duration-200 ease-out',
        blockInset ? 'pl-5 pr-5' : 'pl-4 pr-4',
        selected ? 'bg-bg-weak-50' : 'hover:bg-bg-weak-50',
      )}
    >
      {/* Место под галочку занято ВСЕГДА, показана она не всегда: иначе строка дёргалась бы
          вбок под курсором, и человек попадал бы мимо той, на которую целился.

          Прячем прозрачностью, а не `visibility` и не удалением из разметки: скрытая этими
          двумя способами галочка исчезает и для чтения с экрана, а выбирать строки голосом
          человек имеет право ровно так же. Нажатия на невидимую при этом не проходят. */}
      <span
        data-выбор-строки=''
        className={cn(
          'shrink-0 transition-opacity duration-200 ease-out',
          'group-hover/row:pointer-events-auto group-hover/row:opacity-100',
          'group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100',
          // На планшете галочка видна ВСЕГДА, и это не забытая мелочь, а вынужденный размен.
          //
          // Наведения там не существует: показать галочку нечем, и массовая смена статуса
          // становится недостижимой вовсе — вместе с подвалом списка, где она живёт. Цена
          // известна и замерена 04.09.2026: 286 пустых квадратов вдоль всего списка при
          // действии, которое нужно раз в неделю. На мыши эта цена была лишней, потому что
          // наведение галочку и так открывает; на пальце выбор стоит между «видно всегда» и
          // «нельзя никогда».
          !selectVisible && 'pointer-events-none opacity-0 тач:pointer-events-auto тач:opacity-100',
        )}
      >
        {select}
      </span>
      {body}
    </div>
  );
}

/** Скелет списка на первую загрузку: показывается ТОЛЬКО когда данных нет даже в кэше. */
export function ListSkeleton({
  rows = 6,
  blockInset = false,
}: {
  rows?: number;
  /** Поле 20 точек, как у `ListRow` внутри блока. */
  blockInset?: boolean;
}) {
  return (
    <div aria-hidden='true' className='flex flex-col'>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className={cn(
            'flex flex-col gap-1.5 border-b border-stroke-soft-200 py-3',
            blockInset ? 'px-5' : 'px-4',
          )}
        >
          {/* Тот же оттенок, что у общего скелета: на сером фоне слабый не виден вовсе. */}
          <div className='h-3.5 w-2/3 animate-pulse rounded-md bg-bg-soft-200' />
          <div className='h-3 w-5/6 animate-pulse rounded-md bg-bg-soft-200' />
        </div>
      ))}
    </div>
  );
}
